import { Configuration, LogLevel, PublicClientApplication } from '@azure/msal-browser';

const origin = typeof window !== 'undefined' ? window.location.origin : 'https://silisense.digital';

/** Client ID from env; required for Azure AD sign-in. Empty causes AADSTS900144. */
const clientId = import.meta.env.VITE_SSO_CLIENT_ID || '';

/** True if SSO can be used (client_id is set). If false, Sign in will show config error. */
export function isSsoConfigured(): boolean {
  return clientId.length > 0;
}

/** SAML: backend URL that redirects to IdP (e.g. /v1/auth/saml/login). Set VITE_SAML_LOGIN_URL to enable. */
const samlLoginUrl = import.meta.env.VITE_SAML_LOGIN_URL || '';

export function isSamlConfigured(): boolean {
  return samlLoginUrl.length > 0;
}

/** Full URL for SAML login (same-origin or absolute). */
export function getSamlLoginUrl(): string {
  if (!samlLoginUrl) return '';
  if (samlLoginUrl.startsWith('http')) return samlLoginUrl;
  const base = import.meta.env.VITE_API_BASE_URL || (typeof window !== 'undefined' ? window.location.origin : '');
  return `${base.replace(/\/$/, '')}${samlLoginUrl.startsWith('/') ? '' : '/'}${samlLoginUrl}`;
}

/** Email/password auth: enabled by default. Set VITE_EMAIL_AUTH_ENABLED=false to hide email sign-in. */
export function isEmailAuthEnabled(): boolean {
  return import.meta.env.VITE_EMAIL_AUTH_ENABLED !== 'false';
}

/** SessionStorage key for email-login JWT (company-email auth). */
export const EMAIL_TOKEN_KEY = 'silisense_email_token';

/**
 * MSAL configuration for Azure AD / Entra ID SSO.
 * Redirect URI must be registered in Azure AD for silisense.digital.
 */
export const msalConfig: Configuration = {
  auth: {
    clientId,
    authority: `https://login.microsoftonline.com/${import.meta.env.VITE_SSO_TENANT_ID || 'common'}`,
    redirectUri: import.meta.env.VITE_SSO_REDIRECT_URI || origin,
    postLogoutRedirectUri: origin,
    navigateToLoginRequestUrl: true,
  },
  cache: {
    cacheLocation: 'sessionStorage',
    storeAuthStateInCookie: false,
  },
  system: {
    loggerOptions: {
      loggerCallback: (_level: LogLevel, message: string) => {
        if (import.meta.env.DEV) console.debug('[MSAL]', message);
      },
      logLevel: LogLevel.Warning,
    },
  },
};

/** Scopes for SILISENSE API (backend may validate token with same audience) */
export const loginRequest = {
  scopes: ['User.Read', 'openid', 'profile'],
};

/**
 * Optional extra Microsoft Graph scopes for Integrations → Teams (Education).
 * Comma-separated in VITE_TEAMS_GRAPH_EXTRA_SCOPES, e.g. EduAssignments.ReadBasic,EduRoster.Read
 * (see docs/Microsoft-Teams-Integration.md — Entra app must expose these + admin consent).
 */
function parseTeamsExtraScopes(): string[] {
  const raw = String(import.meta.env.VITE_TEAMS_GRAPH_EXTRA_SCOPES ?? '').trim();
  if (!raw) return [];
  return raw
    .split(',')
    .map((s) => s.trim())
    .filter(Boolean);
}

/** MSAL request for Teams connect: base SSO scopes plus optional Education Graph scopes from env. */
export const teamsConnectRequest = {
  scopes: [...new Set([...loginRequest.scopes, ...parseTeamsExtraScopes()])],
};

/** True when build is configured to ask for Teams/Education Graph scopes (not only User.Read). */
export function isTeamsEducationScopesConfigured(): boolean {
  return parseTeamsExtraScopes().length > 0;
}

/** Optional: scope for calling SILISENSE API (must match backend AZURE_CLIENT_ID / AZURE_API_AUDIENCE). Graph User.Read tokens are rejected by the API. */
export const apiRequest = {
  scopes: import.meta.env.VITE_SSO_API_SCOPE ? [import.meta.env.VITE_SSO_API_SCOPE] : loginRequest.scopes,
};

export const msalInstance = new PublicClientApplication(msalConfig);
